/**
 * Basket monitor for the martingale EA book.
 *
 * Orders group into "baskets" by symbol + direction — a symbol's BUY orders
 * are one basket, its SELL orders another, independently. Two distinct danger
 * signatures must both stay visible:
 *  - slow "grinding" baskets, caught by the drawdown channels (A1/A2);
 *  - "coiled-spring" baskets — many orders stacked with almost no drawdown —
 *    caught by the order-count channel (B) regardless of pain.
 *
 * Data reality: the orders table is an event log (rows appear on open/close,
 * floating profit is never updated, and the reporter can drop events), while
 * the account feed carries the live truth per SYMBOL (net profit, open-order
 * count, current price) but no per-order detail and no buy/sell split. So the
 * live feed is authoritative for money and counts, and the synced rows only
 * supply structure: weighted average, first entry, age. Every basket says
 * where its numbers came from.
 */
import { pipSize } from './drawdown';
export const DEFAULT_BASKET_MONITOR_CONFIG = {
    baseLots: 1.0,
    ddPerOrderWatchUsd: 700,
    ddPerOrderYellowUsd: 1050,
    ddPerOrderRedUsd: 1430,
    pipsFromFirstWatch: 150,
    pipsFromFirstRed: 220,
    ordersWatch: 15,
    ordersRed: 20,
    gateWarningPct: -7,
    gateCriticalPct: -10,
    takeProfitPips: 10,
};
const SEVERITY_RANK = {
    none: 0,
    watch: 1,
    yellow: 2,
    depth: 3,
    red: 4,
};
export const basketSeverityRank = (severity) => SEVERITY_RANK[severity];
/** "EURUSD.PRO" and "eurusd" both key as "EURUSD". */
export const normalizeBasketSymbol = (symbol) => symbol.toUpperCase().split('.')[0];
const ddLevelFor = (ddPerOrder, config) => {
    const scale = config.baseLots;
    if (ddPerOrder >= config.ddPerOrderRedUsd * scale)
        return 'red';
    if (ddPerOrder >= config.ddPerOrderYellowUsd * scale)
        return 'yellow';
    if (ddPerOrder >= config.ddPerOrderWatchUsd * scale)
        return 'watch';
    return 'none';
};
const pipsLevelFor = (pipsFromFirst, config) => {
    if (pipsFromFirst === null)
        return 'none';
    if (pipsFromFirst >= config.pipsFromFirstRed)
        return 'red';
    if (pipsFromFirst >= config.pipsFromFirstWatch)
        return 'watch';
    return 'none';
};
const depthLevelFor = (orders, config) => {
    if (orders >= config.ordersRed)
        return 'red';
    if (orders >= config.ordersWatch)
        return 'watch';
    return 'none';
};
const chipToneFor = (level) => level === 'red' ? 'red' : level === 'yellow' ? 'yellow' : 'watch';
const formatUsd = (value) => Math.round(value).toLocaleString('en-US');
/**
 * Live-price re-estimate of a group's net P/L: price move × lots × the
 * pair's derived value factor, on top of the synced costs. Null when any
 * order lacks a value factor or there is no quote.
 */
const estimateGroupNetUsd = (group, currentPrice) => {
    if (currentPrice === null)
        return null;
    let total = 0;
    for (const order of group) {
        if (order.valueFactor === null)
            return null;
        const move = order.side === 'buy' ? currentPrice - order.openPrice : order.openPrice - currentPrice;
        total += move * order.lots * order.valueFactor + order.syncedNetUsd;
    }
    return total;
};
export const computeBasketAccountView = (equityUsd, floatingUsd, config) => {
    const gatePct = equityUsd !== 0 ? (floatingUsd / equityUsd) * 100 : 0;
    const gateLevel = gatePct <= config.gateCriticalPct ? 'critical'
        : gatePct <= config.gateWarningPct ? 'warning'
            : 'none';
    return { equityUsd, floatingUsd, gatePct, gateLevel };
};
const buildBasket = (symbol, facts, feed, config, nowMs, orderDetailLoading) => {
    const { side, group } = facts;
    const syncedOrders = group.length;
    const currentPrice = feed?.currentPrice ?? null;
    const orders = Math.max(facts.liveOrders ?? 0, syncedOrders);
    const partialSync = facts.liveOrders !== null && syncedOrders < facts.liveOrders;
    let netUsd;
    let ddSource;
    const estimate = estimateGroupNetUsd(group, currentPrice);
    if (facts.liveNetUsd !== null) {
        netUsd = facts.liveNetUsd;
        ddSource = 'live';
    }
    else if (estimate !== null) {
        netUsd = estimate;
        ddSource = 'estimated';
    }
    else {
        netUsd = group.reduce((sum, o) => sum + o.syncedNetUsd, 0);
        ddSource = 'synced';
    }
    const ddUsd = Math.min(netUsd, 0);
    const ddPerOrder = orders > 0 ? Math.abs(ddUsd) / orders : 0;
    const totalLots = syncedOrders > 0 ? group.reduce((sum, o) => sum + o.lots, 0) : null;
    const wavgPrice = syncedOrders > 0 && totalLots !== null && totalLots > 0
        ? group.reduce((sum, o) => sum + o.openPrice * o.lots, 0) / totalLots
        : null;
    // The oldest order defines "first entry" even after later orders — and
    // under partial sync the oldest rows are the ones most likely present.
    const oldest = syncedOrders > 0
        ? group.reduce((min, o) => (o.openTime < min.openTime ? o : min), group[0])
        : null;
    const firstEntryPrice = oldest ? oldest.openPrice : null;
    const ageDays = oldest ? Math.max(0, (nowMs / 1000 - oldest.openTime) / 86400) : null;
    const pip = pipSize(symbol);
    let pipsFromWavg = null;
    let pipsFromFirst = null;
    if (currentPrice !== null && wavgPrice !== null) {
        pipsFromWavg = Math.abs(currentPrice - wavgPrice) / pip;
    }
    if (currentPrice !== null && firstEntryPrice !== null && side !== 'unknown') {
        const adverse = side === 'sell' ? currentPrice - firstEntryPrice : firstEntryPrice - currentPrice;
        pipsFromFirst = Math.max(0, adverse / pip);
    }
    let takeProfitPrice = null;
    let pipsToTakeProfit = null;
    let priceToTakeProfit = null;
    if (wavgPrice !== null && side !== 'unknown') {
        const offset = config.takeProfitPips * pip;
        takeProfitPrice = side === 'buy' ? wavgPrice + offset : wavgPrice - offset;
        if (currentPrice !== null) {
            const remaining = side === 'buy' ? takeProfitPrice - currentPrice : currentPrice - takeProfitPrice;
            priceToTakeProfit = remaining;
            pipsToTakeProfit = remaining / pip;
        }
    }
    const ddLevel = ddLevelFor(ddPerOrder, config);
    const pipsLevel = pipsLevelFor(pipsFromFirst, config);
    const depthLevel = depthLevelFor(orders, config);
    const belowYellowPain = ddPerOrder < config.ddPerOrderYellowUsd * config.baseLots;
    const depthNoPain = depthLevel === 'red' && belowYellowPain;
    const chfEmphasis = depthNoPain && symbol.includes('CHF');
    let severity;
    if (ddLevel === 'red' || pipsLevel === 'red' || (depthLevel === 'red' && !depthNoPain)) {
        severity = 'red';
    }
    else if (depthNoPain) {
        severity = 'depth';
    }
    else if (ddLevel === 'yellow') {
        severity = 'yellow';
    }
    else if (ddLevel === 'watch' || pipsLevel === 'watch' || depthLevel === 'watch') {
        severity = 'watch';
    }
    else {
        severity = 'none';
    }
    const chips = [];
    if (ddLevel !== 'none') {
        chips.push({
            channel: 'ddPerOrder',
            tone: chipToneFor(ddLevel),
            label: `DD/ord ${formatUsd(ddPerOrder)}`,
        });
    }
    if (pipsLevel !== 'none' && pipsFromFirst !== null) {
        chips.push({
            channel: 'pipsFromFirst',
            tone: chipToneFor(pipsLevel),
            label: `1st-entry ${Math.round(pipsFromFirst)}p`,
        });
    }
    if (depthLevel !== 'none') {
        chips.push({
            channel: 'depth',
            tone: depthNoPain ? 'depth' : chipToneFor(depthLevel),
            label: `depth ${orders}`,
        });
    }
    return {
        key: `${symbol}:${side}`,
        symbol,
        side,
        orders,
        syncedOrders,
        partialSync,
        totalLots,
        ddUsd,
        ddSource,
        ddPerOrder,
        wavgPrice,
        firstEntryPrice,
        currentPrice,
        pipsFromWavg,
        pipsFromFirst,
        takeProfitPrice,
        pipsToTakeProfit,
        priceToTakeProfit,
        ageDays,
        detailPending: syncedOrders === 0 && orderDetailLoading,
        ddLevel,
        pipsLevel,
        depthLevel,
        depthNoPain,
        chfEmphasis,
        severity,
        chips,
    };
};
export const computeBasketViews = (orders, pairFeeds, config, nowMs, orderDetailLoading) => {
    const feedBySymbol = new Map();
    for (const feed of pairFeeds) {
        feedBySymbol.set(normalizeBasketSymbol(feed.symbol), feed);
    }
    const groupsBySymbol = new Map();
    for (const order of orders) {
        const symbol = normalizeBasketSymbol(order.symbol);
        let sides = groupsBySymbol.get(symbol);
        if (!sides) {
            sides = new Map();
            groupsBySymbol.set(symbol, sides);
        }
        const group = sides.get(order.side);
        if (group) {
            group.push(order);
        }
        else {
            sides.set(order.side, [order]);
        }
    }
    const symbols = new Set(groupsBySymbol.keys());
    feedBySymbol.forEach((feed, symbol) => {
        if ((feed.openOrders ?? 0) > 0)
            symbols.add(symbol);
    });
    const baskets = [];
    symbols.forEach((symbol) => {
        const feed = feedBySymbol.get(symbol);
        const sides = groupsBySymbol.get(symbol) ?? new Map();
        if (sides.size === 0) {
            // Feed shows open orders but no rows ever synced. Money and count are
            // still live and channel B still fires — that's the whole point.
            baskets.push(buildBasket(symbol, {
                side: 'unknown',
                group: [],
                liveNetUsd: feed?.profitUsd ?? null,
                liveOrders: feed?.openOrders ?? null,
            }, feed, config, nowMs, orderDetailLoading));
            return;
        }
        if (sides.size === 1) {
            // The symbol's whole feed snapshot belongs to this one basket.
            sides.forEach((group, side) => {
                baskets.push(buildBasket(symbol, {
                    side,
                    group,
                    liveNetUsd: feed?.profitUsd ?? null,
                    liveOrders: feed?.openOrders ?? null,
                }, feed, config, nowMs, orderDetailLoading));
            });
            return;
        }
        // Both directions open: the feed can't be split by side, so each basket
        // falls back to a live-price estimate over its own synced rows.
        sides.forEach((group, side) => {
            baskets.push(buildBasket(symbol, { side, group, liveNetUsd: null, liveOrders: null }, feed, config, nowMs, orderDetailLoading));
        });
    });
    baskets.sort((a, b) => {
        const bySeverity = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
        if (bySeverity !== 0)
            return bySeverity;
        return Math.abs(b.ddUsd) - Math.abs(a.ddUsd);
    });
    return baskets;
};
/** Take-profit targets keyed by normalized symbol; buy before sell. */
export const computeBasketTakeProfits = (baskets) => {
    const bySymbol = new Map();
    for (const basket of baskets) {
        if (basket.side === 'unknown' || basket.takeProfitPrice === null)
            continue;
        const entry = {
            side: basket.side,
            takeProfitPrice: basket.takeProfitPrice,
            pipsToTakeProfit: basket.pipsToTakeProfit,
            priceToTakeProfit: basket.priceToTakeProfit,
            partialSync: basket.partialSync,
        };
        const list = bySymbol.get(basket.symbol);
        if (list) {
            list.push(entry);
        }
        else {
            bySymbol.set(basket.symbol, [entry]);
        }
    }
    bySymbol.forEach((list) => list.sort((a, b) => (a.side === b.side ? 0 : a.side === 'buy' ? -1 : 1)));
    return bySymbol;
};
